import type { ReactNode } from 'react'
import { Logo } from '@/components/Logo'
import { ModeToggle } from '@/components/mode-toggle'
import { Mascot, MascotVariant } from '@/components/shared/Mascot'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { isTauri } from '@/lib/utils/tauri'

import { AuthScene } from './scene/AuthScene'

export type AuthPageType =
  | 'login'
  | 'register'
  | 'login-confirmation'
  | 'register-confirmation'
  | 'forgot-password'
  | 'reset-password'
  | 'resend-code'

interface SplitAuthLayoutProps {
  children: ReactNode
  pageType?: AuthPageType
  customHeadline?: string
  customSubheadline?: string
}

interface PageContent {
  /** The browser tab: the headline's plain form, no trailing period. */
  tabTitle: string
  headline: string
  subheadline: string
  mascotVariant: MascotVariant
}

const LEGAL_LINKS: { href: string; label: string }[] = [
  { href: 'https://scrambl.com/terms', label: 'Terms' },
  { href: 'https://scrambl.com/privacy', label: 'Privacy' },
  { href: 'https://scrambl.com/eula', label: 'EULA' },
  { href: 'https://scrambl.com/aup', label: 'Acceptable Use' },
  { href: 'https://scrambl.com/disclaimer', label: 'Disclaimer' },
  { href: '/beta', label: 'Beta' },
]

const PAGE_CONTENT: Record<AuthPageType, PageContent> = {
  login: {
    tabTitle: 'Sign in',
    headline: 'Sign in.',
    subheadline:
      "Ready to crack the chaos? Let's get you signed in and on to what matters most.",
    mascotVariant: 'welcome',
  },
  register: {
    tabTitle: 'Sign up',
    headline: 'Join the team.',
    subheadline:
      'Start organizing your work, collaborating with your team, and getting things done.',
    mascotVariant: 'welcome-alt',
  },
  'login-confirmation': {
    tabTitle: 'Check your inbox',
    headline: 'Check your inbox.',
    subheadline:
      "We've sent you a verification code. Enter it below to continue.",
    mascotVariant: 'notifications',
  },
  'register-confirmation': {
    tabTitle: 'Verify your email',
    headline: 'Almost there.',
    subheadline:
      'Verify your email to complete your registration and start exploring.',
    mascotVariant: 'notifications',
  },
  'forgot-password': {
    tabTitle: 'Forgot password',
    headline: 'No worries.',
    subheadline: "We'll help you get back into your account in no time.",
    mascotVariant: 'default',
  },
  'reset-password': {
    tabTitle: 'Reset password',
    headline: 'Create a new password.',
    subheadline: 'Choose a strong password to keep your account secure.',
    mascotVariant: 'award',
  },
  'resend-code': {
    tabTitle: 'Resend verification code',
    headline: 'Get a new code.',
    subheadline:
      "Enter the email you signed up with and we'll send a fresh verification code.",
    mascotVariant: 'notifications',
  },
}

export function SplitAuthLayout({
  children,
  pageType = 'login',
  customHeadline,
  customSubheadline,
}: SplitAuthLayoutProps) {
  const content = PAGE_CONTENT[pageType]
  useDocumentTitle(content.tabTitle)
  const headline = customHeadline || content.headline
  const subheadline = customSubheadline || content.subheadline
  const showModeToggle = !isTauri()
  const year = new Date().getFullYear()

  return (
    <div className="relative flex h-[100svh] overflow-hidden bg-background">
      {/* ───────────────── LEFT — Brand stage ───────────────── */}
      <aside className="relative hidden overflow-hidden lg:flex lg:w-1/2 xl:w-[55%]">
        {/* Office wall + daylight — light */}
        <div
          aria-hidden
          className="absolute inset-0 dark:hidden"
          style={{
            background: `
              radial-gradient(ellipse 55% 50% at 85% 8%, hsl(38 85% 78% / 0.40), transparent 65%),
              radial-gradient(ellipse 60% 45% at 95% 28%, hsl(35 75% 82% / 0.28), transparent 70%),
              radial-gradient(ellipse 45% 60% at 10% 95%, hsl(220 18% 80% / 0.22), transparent 65%),
              radial-gradient(ellipse 80% 25% at 50% 100%, hsl(30 12% 80% / 0.30), transparent 75%),
              linear-gradient(180deg, hsl(36 27% 90%) 0%, hsl(34 23% 86.5%) 55%, hsl(31 19% 82.5%) 100%)
            `,
          }}
        />
        {/* Office wall + daylight — dark */}
        <div
          aria-hidden
          className="absolute inset-0 hidden dark:block"
          style={{
            background: `
              radial-gradient(ellipse 55% 50% at 85% 8%, hsl(38 60% 60% / 0.22), transparent 65%),
              radial-gradient(ellipse 60% 45% at 95% 28%, hsl(35 50% 55% / 0.14), transparent 70%),
              radial-gradient(ellipse 45% 60% at 10% 95%, hsl(220 18% 22% / 0.40), transparent 65%),
              linear-gradient(180deg, hsl(28 12% 10%) 0%, hsl(26 10% 8%) 60%, hsl(24 8% 6%) 100%)
            `,
          }}
        />

        {/* Grain texture — barely there */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.35] mix-blend-soft-light dark:opacity-[0.5]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.55 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")",
          }}
        />

        {/* ─── Crack the chaos: the day scrambles, then sorts itself ─── */}
        <AuthScene />

        {/* Vignette — deepens the corners, focuses the work */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-[3]"
          style={{
            background:
              'radial-gradient(ellipse 78% 72% at 52% 46%, transparent 52%, hsl(28 24% 10% / 0.12) 100%)',
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 z-[3] hidden dark:block"
          style={{
            background:
              'radial-gradient(ellipse 78% 72% at 52% 46%, transparent 46%, hsl(24 30% 2% / 0.45) 100%)',
          }}
        />

        {/* Content overlay (logo + tagline + footer) */}
        <div className="relative z-10 flex w-full flex-col justify-between px-6 pb-6 pt-4 lg:px-8 lg:pb-8 lg:pt-5 xl:px-12 xl:pb-12 xl:pt-6">
          {/* Top — Logo (→ scrambl.com) */}
          <div className="flex h-10 items-center">
            <a
              href="https://scrambl.com"
              className="inline-block transition-opacity hover:opacity-80"
            >
              <Logo
                size={28}
                withText
                textClassName="text-2xl font-light tracking-wide font-kronaone"
              />
            </a>
          </div>

          <div className="flex-1" />

          {/* Footer */}
          {/* Items keep one line ("Acceptable Use", the copyright) and the
              row wraps between them, like the mobile footer. */}
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1.5 text-xs text-muted-foreground">
            <p className="whitespace-nowrap">&copy; {year} Scrambl, LLC</p>
            <nav
              aria-label="Legal"
              className="flex flex-wrap items-center gap-x-4 gap-y-1.5"
            >
              {LEGAL_LINKS.map(link => (
                <a
                  key={link.href}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="whitespace-nowrap transition-colors hover:text-foreground"
                >
                  {link.label}
                </a>
              ))}
            </nav>
          </div>
        </div>
      </aside>

      {/* Vertical seam — subtle */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-0 hidden h-full w-px bg-gradient-to-b from-transparent via-foreground/[0.06] to-transparent lg:block xl:left-[55%]"
      />

      {/* ───────────────── RIGHT — Form ───────────────── */}
      <main className="relative flex h-full flex-1 flex-col overflow-y-auto overflow-x-clip pb-[env(safe-area-inset-bottom)] lg:w-1/2 xl:w-[45%]">
        {/* Mobile ambient gradient */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 lg:hidden"
          style={{
            background: `
              radial-gradient(ellipse 90% 50% at 50% 0%, hsl(var(--brand-green) / 0.10), transparent 60%),
              radial-gradient(ellipse 80% 50% at 50% 100%, hsl(var(--brand-blue) / 0.06), transparent 60%)
            `,
          }}
        />

        {/* Top bar */}
        <div className="relative flex items-center justify-between px-6 pt-4 lg:px-8 lg:pt-5 xl:px-12 xl:pt-6">
          <a
            href="https://scrambl.com"
            className="transition-opacity hover:opacity-80 lg:hidden"
          >
            <Logo
              size={24}
              withText
              textClassName="text-xl font-light tracking-wide font-kronaone"
            />
          </a>
          <div className="hidden lg:block" />
          {showModeToggle && (
            <div className="flex h-10 items-center">
              <ModeToggle />
            </div>
          )}
        </div>

        {/* Form container */}
        <div className="relative flex flex-1 items-center justify-center px-4 py-8 lg:px-8 xl:px-16">
          <div className="w-full max-w-[420px]">
            {/* Header */}
            <div className="mb-8 text-center lg:text-left">
              {/* Mobile-only mascot */}
              <Mascot
                variant={content.mascotVariant}
                size="md"
                animate="float"
                className="mx-auto mb-4 lg:hidden"
              />
              <h1 className="font-heading mb-3 text-[2rem] font-semibold leading-[1.1] tracking-tight text-foreground md:text-[2.25rem]">
                {headline}
              </h1>
              <p className="text-[15px] leading-relaxed text-muted-foreground">
                {subheadline}
              </p>
            </div>

            {/* Form content */}
            {children}
          </div>
        </div>

        {/* Mobile footer */}
        <div className="relative px-4 pb-6 pt-4 text-center lg:hidden">
          <nav
            aria-label="Legal"
            className="mb-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-xs text-muted-foreground"
          >
            {LEGAL_LINKS.map(link => (
              <a
                key={link.href}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap transition-colors hover:text-foreground"
              >
                {link.label}
              </a>
            ))}
          </nav>
          <p className="text-xs text-muted-foreground">
            &copy; {year} Scrambl, LLC
          </p>
        </div>
      </main>
    </div>
  )
}
